import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { catchError, debounceTime, filter, forkJoin, map, merge, of, switchMap } from 'rxjs';
import { WorkflowService } from '../api/workflow.service';
import { clearOnUserChange } from '../auth/user-scope';
import { ContextService } from '../context/context.service';
import { CAPEX_APP_CODE } from '../models/capex.models';
import { Application } from '../models/org.models';

/** The workspace shared by all apps: people, organisation, org import and the applications list. */
export const ORG_WORKSPACE = 'ORG';

/** Pages that belong to one application and carry it in the address as ?app=CODE. */
const APP_SCOPED_PAGES = ['/inbox', '/notifications', '/admin/forms', '/admin/workflows', '/admin/access'];
/** App setup pages: only for the application's admins. */
const APP_SETUP_PAGES = ['/admin/forms', '/admin/workflows', '/admin/access'];
/** Pages of the shared Organisation workspace. */
const ORG_PAGES = ['/admin/people', '/admin/organisation', '/admin/org-import', '/admin/applications'];

const STORAGE_KEY = 'ebp.workspace';
const LAST_APP_KEY = 'ebp.workspace.lastApp';

/**
 * The workspace the user is working in: one application (CAPEX, MDM...) or the shared Organisation area, like
 * switching workspace in Slack. The address decides it (/capex, /apps/MDM, ?app=MDM, /admin/people...); pages that
 * belong to no workspace (a request, notifications) keep the current one. Sign-in always lands on the chooser (/).
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceService {
  private readonly context = inject(ContextService);
  private readonly router = inject(Router);
  private readonly workflow = inject(WorkflowService);

  /** The current workspace's code: an application code, ORG_WORKSPACE, or null before one is chosen. */
  readonly code = signal<string | null>(read(STORAGE_KEY));
  /** The last application worked in (kept while in the Organisation workspace, e.g. for People). */
  readonly lastAppCode = signal<string | null>(read(LAST_APP_KEY));
  /** Approvals waiting for the user, per application code. */
  readonly counts = signal<Record<string, number>>({});

  /** The applications on the rail: the ones the user may open, and the ones they set up (inactive ones last). */
  readonly apps = computed<Application[]>(() => {
    const accessible = new Set(this.context.context()?.accessibleApplicationIds ?? []);
    return this.context
      .apps()
      .filter((app) => accessible.has(app.id) || this.context.managedAppIds().includes(app.id))
      .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  });
  /** Administrators and app admins also get the shared Organisation workspace. */
  readonly hasOrg = this.context.canAdminister;
  readonly isOrg = computed(() => this.code() === ORG_WORKSPACE && this.hasOrg());
  /** The current application, or null in the Organisation workspace or before one is chosen. */
  readonly app = computed(() => this.find(this.code()));
  readonly lastApp = computed(() => this.find(this.lastAppCode()));
  /** True once an application (or Organisation) is chosen; on the chooser nothing is. */
  readonly selected = computed(() => this.isOrg() || !!this.app());
  constructor() {
    clearOnUserChange(() => this.counts.set({}));

    const destroyRef = inject(DestroyRef);
    const navigations = this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd));
    navigations.pipe(takeUntilDestroyed(destroyRef)).subscribe((e) => this.syncFromUrl(e.urlAfterRedirects));

    // Badges: loaded with the apps and refreshed after each navigation (a decision changes them), one small query per app.
    merge(navigations, toObservable(this.apps))
      .pipe(
        debounceTime(300),
        switchMap(() => this.loadCounts()),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((counts) => this.counts.set(counts));
  }

  /** Approvals waiting in an application. */
  count(code: string): number {
    return this.counts()[code.toUpperCase()] ?? 0;
  }

  /** True when the user sets up this application (forms, workflows, app access). */
  canSetUp(app: Application): boolean {
    return this.context.managesAppId(app.id);
  }

  /** True when the user may open the application's requests page. */
  canOpen(app: Application): boolean {
    return this.context.canOpen(app.code);
  }

  /**
   * Opens a workspace. On a page that every app has (approvals, forms, workflows, app access) it stays on that page
   * for the new app, otherwise it opens the app's requests page (or People for the Organisation workspace).
   */
  open(code: string): Promise<boolean> {
    return this.router.navigateByUrl(this.urlFor(code));
  }

  /**
   * For the app setup pages: follows the page's ?app=CODE among the apps it lists and calls `load` with that app's
   * id. Without ?app the workspace's app (or the first one) is put in the address, which loads it.
   */
  followAppParam(
    route: ActivatedRoute,
    apps: readonly Application[],
    load: (appId: string) => void,
    destroyRef: DestroyRef,
  ): void {
    route.queryParamMap.pipe(takeUntilDestroyed(destroyRef)).subscribe((params) => {
      const wanted = params.get('app')?.toUpperCase();
      const app = wanted ? apps.find((a) => a.code.toUpperCase() === wanted) : undefined;
      if (app) return load(app.id);

      const fallback = apps.find((a) => a.id === this.lastApp()?.id) ?? apps[0];
      if (fallback) void this.router.navigate([], { relativeTo: route, queryParams: { app: fallback.code }, replaceUrl: true });
    });
  }

  /** Where a workspace opens from the current page. */
  urlFor(code: string): string {
    if (code === ORG_WORKSPACE) return '/admin/people';

    const app = this.find(code);
    if (!app) return '/';
    const path = this.router.url.split(/[?#]/)[0];
    const stays = APP_SCOPED_PAGES.includes(path) && (!APP_SETUP_PAGES.includes(path) || this.canSetUp(app));
    if (stays) return this.router.createUrlTree([path], { queryParams: { app: app.code } }).toString();
    return this.canOpen(app) ? `${this.context.routeFor(app)}/dashboard` : `/inbox?app=${encodeURIComponent(app.code)}`;
  }

  private syncFromUrl(url: string): void {
    const tree = this.router.parseUrl(url);
    const path = url.split(/[?#]/)[0];
    const fromQuery = tree.queryParamMap.get('app');
    const appPath = /^\/apps\/([^/]+)/.exec(path);

    // The chooser: no workspace is selected until one is picked.
    if (path === '/') return this.clear();

    let code: string | null = null;
    if (path === '/capex' || path.startsWith('/capex/')) code = CAPEX_APP_CODE;
    else if (appPath) code = decodeURIComponent(appPath[1]);
    else if (fromQuery && APP_SCOPED_PAGES.includes(path)) code = fromQuery;
    else if (ORG_PAGES.includes(path)) code = ORG_WORKSPACE;
    if (code) this.set(code.toUpperCase());
  }

  private set(code: string): void {
    this.code.set(code);
    write(STORAGE_KEY, code);
    if (code !== ORG_WORKSPACE) {
      this.lastAppCode.set(code);
      write(LAST_APP_KEY, code);
    }
  }

  private clear(): void {
    this.code.set(null);
    remove(STORAGE_KEY);
  }

  private find(code: string | null): Application | null {
    if (!code || code === ORG_WORKSPACE) return null;
    return this.apps().find((a) => a.code.toUpperCase() === code.toUpperCase()) ?? null;
  }

  private loadCounts() {
    const apps = this.apps();
    if (!apps.length) return of({} as Record<string, number>);
    return forkJoin(
      apps.map((app) =>
        this.workflow.myTasks({ applicationCode: app.code, pageSize: 1 }).pipe(
          map((page) => [app.code.toUpperCase(), page.totalCount] as const),
          catchError(() => of([app.code.toUpperCase(), 0] as const)),
        ),
      ),
    ).pipe(map((pairs) => Object.fromEntries(pairs) as Record<string, number>));
  }
}

/** Browser storage can be blocked (private windows, policies): the workspace then just isn't remembered. */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // nothing stored
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // not remembered; nothing else depends on it
  }
}
